import { useCallback } from "react";
import { useRouter } from "expo-router";
import { HeaderBackButton } from "@react-navigation/elements";
import { useUnistyles } from "react-native-unistyles";

/**
 * Hook that provides a standardized header back button for screens that need explicit back navigation.
 *
 * This is needed for screens under the tenant Stack navigator when navigating from outside
 * (e.g., from the Alarms tab), where the default back button doesn't appear because the screen
 * is the first in that Stack's history.
 *
 * @param fallbackRoute - Route to navigate to if router.canGoBack() returns false (defaults to "/alarms")
 * @returns headerLeft - A memoized component to pass to navigation.setOptions({ headerLeft })
 */
export function useHeaderBackButton(fallbackRoute: string = "/alarms") {
  const router = useRouter();
  const { theme } = useUnistyles();

  const handleBackPress = useCallback(() => {
    if (router.canGoBack()) {
      router.back();
      return;
    }
    router.dismissTo(fallbackRoute as never);
  }, [router, fallbackRoute]);

  const headerLeft = useCallback(
    () => <HeaderBackButton onPress={handleBackPress} tintColor={theme.colors.text} />,
    [handleBackPress, theme.colors.text],
  );

  return { headerLeft };
}
